import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import {
  useAdminSubawardeeFilterStore,
} from '../stores/adminSubawardeeFilterStore';
import { isNonEmptySubawardeeId } from '../utils/adminSubawardeeUtils';
import {
  useAdminMilestoneViewStore,
  type AdminMilestoneView,
} from '../stores/adminMilestoneViewStore';
import { useAdminComponentProjectFilterStore } from '../stores/adminComponentProjectFilterStore';
import { isKnownComponentProject } from '../config/componentProjects';
import { isAdminMilestonesRoute } from '../utils/adminSubawardeePaths';

function parseViewParam(value: string | null): AdminMilestoneView {
  return value === 'component' ? 'component' : 'subawardee';
}

export interface AdminMilestoneViewState {
  view: AdminMilestoneView;
  setView: (view: AdminMilestoneView) => void;
  /** Shared navbar component project filter (persisted). */
  componentProject: string | null;
  setComponentProject: (value: string | null) => void;
  subawardee: string | null;
  setSubawardee: (value: string | null) => void;
}

/**
 * Milestones admin scope: view mode (component vs sub-awardee), persisted and synced with URL on `/admin/milestones`.
 * Component project comes from the shared navbar filter store; sub-awardee from the reports admin filter store.
 */
export function useAdminMilestoneViewUrlState(): AdminMilestoneViewState {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const view = useAdminMilestoneViewStore((s) => s.view);
  const setViewStore = useAdminMilestoneViewStore((s) => s.setView);
  const componentProject = useAdminComponentProjectFilterStore((s) => s.componentProject);
  const setComponentProjectStore = useAdminComponentProjectFilterStore((s) => s.setComponentProject);

  const subawardee = useAdminSubawardeeFilterStore((s) => s.subawardee);
  const setSubawardeeStore = useAdminSubawardeeFilterStore((s) => s.setSubawardee);

  const onMilestonesRoute = isAdminMilestonesRoute(location.pathname);
  const lastUrlProjectRef = useRef<string | null>(null);

  // Deep link / browser history → stores (component project: navbar store is authoritative;
  // only apply URL when the param itself changes, e.g. back/forward or external link).
  useEffect(() => {
    if (!onMilestonesRoute) return;

    const viewFromUrl = parseViewParam(searchParams.get('view'));
    const hasSubawardeeParam = isNonEmptySubawardeeId(searchParams.get('subawardee'));
    const resolvedView =
      searchParams.get('view') == null && hasSubawardeeParam ? 'subawardee' : viewFromUrl;

    if (resolvedView !== view) {
      setViewStore(resolvedView);
    }

    const projectFromUrl = searchParams.get('component_project');
    if (
      isKnownComponentProject(projectFromUrl) &&
      projectFromUrl !== lastUrlProjectRef.current
    ) {
      lastUrlProjectRef.current = projectFromUrl;
      if (projectFromUrl !== componentProject) {
        setComponentProjectStore(projectFromUrl);
      }
    } else if (!isKnownComponentProject(projectFromUrl)) {
      lastUrlProjectRef.current = null;
    }

    const subFromUrl = searchParams.get('subawardee');
    if (isNonEmptySubawardeeId(subFromUrl) && subFromUrl !== subawardee) {
      setSubawardeeStore(subFromUrl);
    }
  }, [
    searchParams,
    onMilestonesRoute,
    view,
    componentProject,
    subawardee,
    setViewStore,
    setComponentProjectStore,
    setSubawardeeStore,
  ]);

  const syncUrl = useCallback(
    (next: {
      view: AdminMilestoneView;
      subawardee: string | null;
    }) => {
      if (!onMilestonesRoute) return;
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set('view', next.view);
          params.delete('component_project');
          if (next.view === 'subawardee') {
            if (next.subawardee && isNonEmptySubawardeeId(next.subawardee)) {
              params.set('subawardee', next.subawardee);
            } else {
              params.delete('subawardee');
            }
          } else {
            params.delete('subawardee');
          }
          return params;
        },
        { replace: true }
      );
    },
    [onMilestonesRoute, setSearchParams]
  );

  // Store has selection but URL is missing params (e.g. navbar link)
  useEffect(() => {
    if (!onMilestonesRoute) return;
    const hasView = searchParams.has('view');
    const hasScope =
      (view === 'component' && isKnownComponentProject(componentProject)) ||
      view === 'subawardee';
    if (hasView && hasScope) return;

    const shouldSyncComponent =
      view === 'component' && isKnownComponentProject(componentProject);
    const shouldSyncSubawardee = view === 'subawardee' && isNonEmptySubawardeeId(subawardee);
    if (!shouldSyncComponent && !shouldSyncSubawardee && hasView) return;

    syncUrl({ view, subawardee });
  }, [view, componentProject, subawardee, onMilestonesRoute, searchParams, syncUrl]);

  const setView = useCallback(
    (value: AdminMilestoneView) => {
      setViewStore(value);
      syncUrl({ view: value, subawardee });
    },
    [setViewStore, syncUrl, subawardee]
  );

  const setComponentProject = useCallback(
    (value: string | null) => {
      setComponentProjectStore(value);
    },
    [setComponentProjectStore]
  );

  const setSubawardee = useCallback(
    (value: string | null) => {
      setSubawardeeStore(value);
      const nextView = value ? 'subawardee' : view;
      if (value) setViewStore('subawardee');
      syncUrl({ view: nextView, subawardee: value });
    },
    [setSubawardeeStore, setViewStore, syncUrl, view]
  );

  return {
    view,
    setView,
    componentProject,
    setComponentProject,
    subawardee,
    setSubawardee,
  };
}
